import { useEffect, useState } from "react";
import { NavLink, Route, Routes } from "react-router-dom";
import { BarChart3, Flag, Image, LayoutDashboard, ListOrdered, Loader2, LogOut, Settings, Users, Vote } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/tss/Ornaments";
import AdminLogin from "@/pages/admin/AdminLogin";
import Dashboard from "@/pages/admin/Dashboard";
import Committees from "@/pages/admin/Committees";
import Categories from "@/pages/admin/Categories";
import HeroAdmin from "@/pages/admin/HeroAdmin";
import Results from "@/pages/admin/Results";
import Fraud from "@/pages/admin/Fraud";
import SettingsPage from "@/pages/admin/SettingsPage";
import Votes from "@/pages/admin/Votes";

const NAV = [
  ["", "Dashboard", LayoutDashboard],
  ["committees", "Committees", Users],
  ["categories", "Categories", ListOrdered],
  ["hero", "Hero & Posters", Image],
  ["results", "Results", BarChart3],
  ["votes", "Votes", Vote],
  ["fraud", "Fraud log", Flag],
  ["settings", "Settings", Settings],
];

const Nav = () => (
  <nav className="flex gap-1 overflow-x-auto px-3 pb-3 [scrollbar-width:none] lg:flex-col lg:overflow-visible lg:px-3">
    {NAV.map(([path, label, Icon]) => (
      <NavLink
        key={path}
        end
        to={`/admin${path ? `/${path}` : ""}`}
        data-testid={`admin-nav-${path || "dashboard"}`}
        className={({ isActive }) =>
          `flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-gold text-maroon-deep" : "text-ivory/75 hover:bg-white/10 hover:text-ivory"}`
        }
      >
        <Icon className="h-4 w-4" /> {label}
      </NavLink>
    ))}
  </nav>
);

export default function AdminApp() {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    api.get("/admin/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
    const id = api.interceptors.response.use(undefined, (err) => {
      if (err?.response?.status === 401 && err.config?.url?.startsWith("/admin/") && err.config.url !== "/admin/login") setAuthed(false);
      return Promise.reject(err);
    });
    return () => api.interceptors.response.eject(id);
  }, []);

  if (authed === null) return <div className="grid min-h-screen place-items-center bg-[#1C100B]"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;
  if (!authed) return <AdminLogin onLogin={() => setAuthed(true)} />;

  const logout = async () => { await api.post("/admin/logout"); setAuthed(false); };

  return (
    <div className="min-h-screen bg-ivory lg:flex" data-testid="admin-shell">
      <aside className="sticky top-0 z-30 bg-[#1C100B] lg:h-screen lg:w-60 lg:shrink-0">
        <div className="flex items-center gap-3 px-4 py-3 lg:py-6">
          <Logo size={34} />
          <div className="min-w-0 flex-1">
            <p className="font-display text-base font-bold leading-tight text-gold-light">TSS Admin</p>
            <p className="text-[11px] text-ivory/60">Sharod Shamman 2026</p>
          </div>
          <button type="button" onClick={logout} data-testid="admin-logout-btn" className="rounded-lg p-2 text-ivory/70 hover:bg-white/10 lg:hidden" aria-label="Log out"><LogOut className="h-4 w-4" /></button>
        </div>
        <Nav />
        <button type="button" onClick={logout} data-testid="admin-logout-btn-desktop" className="absolute bottom-6 left-3 hidden items-center gap-2 rounded-lg px-3 py-2 text-sm text-ivory/70 hover:bg-white/10 lg:flex">
          <LogOut className="h-4 w-4" /> Log out
        </button>
      </aside>
      <main className="min-w-0 flex-1 px-4 py-6 lg:px-10 lg:py-10">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="committees" element={<Committees />} />
          <Route path="categories" element={<Categories />} />
          <Route path="hero" element={<HeroAdmin />} />
          <Route path="results" element={<Results />} />
          <Route path="votes" element={<Votes />} />
          <Route path="fraud" element={<Fraud />} />
          <Route path="settings" element={<SettingsPage />} />
        </Routes>
      </main>
    </div>
  );
}
